import React from 'react';
import { Box, Tabs, Tab, TextField, IconButton, List, ListItem, ListItemText, Stack, Button, Divider, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { normalizePhone, formatPhoneKorean } from '../utils/phone';
import { db } from '../firebaseConfig';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, setDoc, getDocs } from 'firebase/firestore';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';

type Props = {
  mode: 'single' | 'bulk';
  onPickPhone: (phone: string) => void;
  onAppendPhones: (phones: string[]) => void;
};

const SmsAddressBookPanel: React.FC<Props> = ({ mode, onPickPhone, onAppendPhones }) => {
  const [tab, setTab] = React.useState<'contacts' | 'groups'>('contacts');
  const [search, setSearch] = React.useState('');

  // 스켈레톤 데이터 (추후 Firestore 연동)
  const [contacts, setContacts] = React.useState<Array<{ id: string; name: string; phone: string }>>([]);
  const [groups, setGroups] = React.useState<Array<{ id: string; name: string; count: number }>>([]);
  const [groupDialogOpen, setGroupDialogOpen] = React.useState(false);
  const [groupName, setGroupName] = React.useState('');
  const [selectedGroup, setSelectedGroup] = React.useState<{ id: string; name: string } | null>(null);
  const [memberDialogOpen, setMemberDialogOpen] = React.useState(false);
  const [members, setMembers] = React.useState<Array<{ id: string; name: string; phone: string; phoneE164: string }>>([]);
  const [contactPickerOpen, setContactPickerOpen] = React.useState(false);
  const [pickedIds, setPickedIds] = React.useState<Set<string>>(new Set());
  const [groupEditOpen, setGroupEditOpen] = React.useState(false);
  const [groupEditName, setGroupEditName] = React.useState('');
  const [editingGroupId, setEditingGroupId] = React.useState<string | null>(null);

  // 연락처 선택 다이얼로그 확장(검색/페이지)
  const [cpSearch, setCpSearch] = React.useState('');
  const [cpPage, setCpPage] = React.useState(0);
  const pageSize = 20;

  // 다이얼로그 상태
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<{ id?: string; name: string; phone: string } | null>(null);
  const [form, setForm] = React.useState<{ name: string; phone: string }>({ name: '', phone: '' });

  const filteredContacts = contacts.filter(c => {
    const q = search.trim();
    if (!q) return true;
    return c.name.includes(q) || normalizePhone(c.phone).includes(normalizePhone(q));
  });
  const filteredGroups = groups.filter(g => {
    const q = search.trim();
    if (!q) return true;
    return g.name.includes(q);
  });

  const handleContactClick = (phone: string) => {
    if (mode === 'single') {
      onPickPhone(formatPhoneKorean(phone));
    } else {
      onAppendPhones([formatPhoneKorean(phone)]);
    }
  };

  // Firestore 구독 (초기 1단계: 단순 전체 목록)
  React.useEffect(() => {
    const qy = query(collection(db, 'sms_contacts'), orderBy('name'));
    const unsub = onSnapshot(qy, (snap) => {
      const list: Array<{ id: string; name: string; phone: string }> = [];
      snap.forEach(d => {
        const data = d.data() as any;
        list.push({ id: d.id, name: data.name || '', phone: data.phone || '' });
      });
      setContacts(list);
    });
    return () => unsub();
  }, []);

  // 그룹 목록 구독
  React.useEffect(() => {
    const qy = query(collection(db, 'sms_groups'), orderBy('name'));
    const unsub = onSnapshot(qy, (snap) => {
      const result: Array<{ id: string; name: string; count: number }> = [];
      snap.forEach(d => {
        const data = d.data() as any;
        result.push({ id: d.id, name: data.name || '', count: data.memberCount || 0 });
      });
      setGroups(result);
    });
    return () => unsub();
  }, []);

  // 선택된 그룹의 멤버 구독
  React.useEffect(() => {
    if (!selectedGroup) return;
    const unsub = onSnapshot(collection(db, 'sms_groups', selectedGroup.id, 'members'), (snap) => {
      const list: Array<{ id: string; name: string; phone: string; phoneE164: string }> = [];
      snap.forEach(d => {
        const data = d.data() as any;
        list.push({ id: d.id, name: data.nameSnapshot || '', phone: data.phoneSnapshot || '', phoneE164: data.phoneE164 || '' });
      });
      setMembers(list);
    });
    return () => unsub();
  }, [selectedGroup]);

  const hotMembersRef = React.useRef<any>(null);
  const [selectedRows, setSelectedRows] = React.useState<number[]>([]);
  const [lastClickedRow, setLastClickedRow] = React.useState<number | null>(null);

  const appendGroupPhones = async (groupId: string) => {
    try {
      const snap = await getDocs(collection(db, 'sms_groups', groupId, 'members'));
      const phones: string[] = [];
      snap.forEach(d => {
        const data = d.data() as any;
        const p = data.phoneSnapshot || data.phone || data.phoneE164 || '';
        if (p) phones.push(formatPhoneKorean(p));
      });
      if (phones.length > 0) onAppendPhones(phones);
    } catch (e) {
      // noop
    }
  };

  // 임시 CRUD(다음 단계에서 다이얼로그로 대체)
  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', phone: '' });
    setDialogOpen(true);
  };
  const openEdit = (c: { id: string; name: string; phone: string }) => {
    setEditing({ id: c.id, name: c.name, phone: c.phone });
    setForm({ name: c.name, phone: c.phone });
    setDialogOpen(true);
  };
  const handleSave = async () => {
    const name = (form.name || '').trim();
    const phone = formatPhoneKorean(form.phone || '');
    const phoneE164 = normalizePhone(form.phone || '');
    if (!name || !phoneE164) return;
    if (editing?.id) {
      await updateDoc(doc(db, 'sms_contacts', editing.id), { name, phone, phoneE164, updatedAt: Date.now() });
    } else {
      await addDoc(collection(db, 'sms_contacts'), { name, phone, phoneE164, createdAt: Date.now() });
    }
    setDialogOpen(false);
  };
  const quickDelete = async (id: string) => {
    if (!window.confirm('삭제할까요?')) return;
    await deleteDoc(doc(db, 'sms_contacts', id));
  };

  return (
    <>
    <Box sx={{ width: 360, border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#fff' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
        <Tab value="contacts" label="연락처" />
        <Tab value="groups" label="그룹" />
      </Tabs>
      <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', display: 'flex', gap: 1 }}>
        <SearchIcon sx={{ color: '#888', mt: '6px' }} />
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          placeholder={tab === 'contacts' ? '이름/번호 검색' : '그룹 검색'}
          fullWidth
        />
        <IconButton size="small" title="추가" onClick={() => (tab === 'contacts' ? openCreate() : setGroupDialogOpen(true))}><AddIcon fontSize="small" /></IconButton>
      </Box>
      {tab === 'contacts' ? (
        <Box sx={{ height: 420, overflowY: 'auto' }}>
          <List dense>
            {filteredContacts.map(c => (
              <ListItem key={c.id} secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" onClick={()=>openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={()=>quickDelete(c.id)}><DeleteIcon fontSize="small" /></IconButton>
                </Stack>
              }>
                <ListItemText
                  primary={c.name}
                  secondary={
                    <Button variant="text" size="small" onClick={() => handleContactClick(c.phone)}>
                      {formatPhoneKorean(c.phone)}
                    </Button>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : (
        <Box sx={{ height: 420, overflowY: 'auto' }}>
          <List dense>
            {filteredGroups.map(g => (
              <ListItem key={g.id}>
                {/* 좌측: 그룹명 + 액션 아이콘들(플러스=멤버 관리, 연필=이름 수정, 휴지통=삭제) */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ListItemText primary={`${g.name} (${g.count})`} />
                    <IconButton size="small" title="멤버 관리" onClick={(e)=>{ e.stopPropagation(); setSelectedGroup({ id: g.id, name: g.name }); setMemberDialogOpen(true); }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="그룹명 수정" onClick={(e)=>{ e.stopPropagation(); setEditingGroupId(g.id); setGroupEditName(g.name); setGroupEditOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" title="그룹 삭제" onClick={async (e)=>{
                      e.stopPropagation();
                      if (!window.confirm('그룹을 삭제할까요?')) return;
                      await deleteDoc(doc(db, 'sms_groups', g.id));
                    }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {/* 우측: 수신번호 목록에 추가 */}
                  <Box>
                    {mode === 'bulk' && (
                      <Button size="small" color="primary" title="수신번호 목록에 추가" endIcon={<ArrowForwardIcon fontSize="small" />} onClick={async (e)=>{ e.stopPropagation(); await appendGroupPhones(g.id); }}>추가</Button>
                    )}
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>

    {/* 연락처 추가/수정 다이얼로그 */}
    <Dialog open={dialogOpen} onClose={()=>setDialogOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{editing ? '연락처 수정' : '연락처 추가'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="이름"
            value={form.name}
            onChange={(e)=> setForm(v=>({ ...v, name: e.target.value }))}
            size="small"
            autoFocus
          />
          <TextField
            label="전화번호"
            placeholder="010-1234-5678"
            value={form.phone}
            onChange={(e)=> setForm(v=>({ ...v, phone: e.target.value }))}
            size="small"
            helperText="숫자만/하이픈 모두 허용"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={()=>setDialogOpen(false)}>취소</Button>
        <Button variant="contained" onClick={handleSave}>저장</Button>
      </DialogActions>
    </Dialog>

    {/* 그룹 추가 다이얼로그 */}
    <Dialog open={groupDialogOpen} onClose={()=>setGroupDialogOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>그룹 추가</DialogTitle>
      <DialogContent>
        <TextField
          placeholder="그룹명"
          value={groupName}
          onChange={(e)=> setGroupName(e.target.value)}
          size="small"
          variant="outlined"
          fullWidth
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={()=>setGroupDialogOpen(false)}>취소</Button>
        <Button variant="contained" onClick={async ()=>{
          const name = (groupName || '').trim();
          if (!name) return;
          await addDoc(collection(db, 'sms_groups'), { name, memberCount: 0, createdAt: Date.now() });
          setGroupDialogOpen(false);
        }}>저장</Button>
      </DialogActions>
    </Dialog>

    {/* 그룹 멤버 관리 다이얼로그 */}
    <Dialog open={memberDialogOpen} onClose={()=>setMemberDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>{selectedGroup ? `${selectedGroup.name} 멤버` : '멤버'}</DialogTitle>
      <DialogContent>
        {/* HOT 멤버 테이블 */}
        <Box sx={{ border: '1px solid #eee', borderRadius: 1, overflow: 'hidden' }}>
          <HotTable
            ref={hotMembersRef}
            data={members.map(m => [m.name, m.phone])}
            colHeaders={[ '이름', '전화번호' ]}
            rowHeaders={true}
            width="100%"
            height="300px"
            licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
            readOnly={true}
            stretchH="all"
            afterSelection={(r: number, c: number, r2: number) => {
              const start = Math.min(r, r2);
              const end = Math.max(r, r2);
              const rows: number[] = [];
              for (let i = start; i <= end; i++) rows.push(i);
              setSelectedRows(rows);
            }
            }
            afterOnCellMouseDown={(e: any, coords: any) => {
              if (typeof coords?.row === 'number' && coords.row >= 0) {
                setLastClickedRow(coords.row);
                setSelectedRows([coords.row]);
              }
            }}
          />
        </Box>
        <Divider sx={{ my: 1 }} />
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={()=> setContactPickerOpen(true)}>주소록에서 추가</Button>
          <Button variant="outlined" color="error" size="small" onClick={async ()=>{
            if (!selectedGroup || !hotMembersRef.current) return;
            // 우선 state 기반으로 판단 (가장 안정적)
            let targets: number[] = selectedRows;
            if ((!targets || targets.length === 0) && lastClickedRow != null) {
              targets = [lastClickedRow];
            }
            targets = (targets || []).filter(i => i >= 0 && i < members.length);
            if (targets.length === 0) return;
            if (!window.confirm('선택한 멤버를 삭제할까요?')) return;
            // 선택된 행들을 역순으로 삭제
            const delOrder = Array.from(new Set(targets)).sort((a,b)=>b-a);
            for (const i of delOrder) {
              const m = members[i];
              if (!m) continue;
              await deleteDoc(doc(db, 'sms_groups', selectedGroup.id, 'members', m.id));
            }
            const newCount = Math.max(0, (members?.length || 0) - delOrder.length);
            await updateDoc(doc(db, 'sms_groups', selectedGroup.id), { memberCount: newCount });
            setSelectedRows([]);
            setLastClickedRow(null);
          }}>선택 삭제</Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={()=>setMemberDialogOpen(false)}>닫기</Button>
      </DialogActions>
    </Dialog>

    {/* 주소록 선택 다이얼로그(간단 버전) */}
    <Dialog open={contactPickerOpen} onClose={()=>setContactPickerOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>주소록에서 선택</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <SearchIcon sx={{ color: '#888', mt: '6px' }} />
          <TextField size="small" placeholder="이름/번호 검색" value={cpSearch} onChange={(e)=>{ setCpSearch(e.target.value); setCpPage(0); }} fullWidth />
        </Stack>
        {(() => {
          const filtered = contacts.filter(c => {
            const q = cpSearch.trim();
            if (!q) return true;
            return c.name.includes(q) || normalizePhone(c.phone).includes(normalizePhone(q));
          });
          const total = filtered.length;
          const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);
          const cur = Math.min(cpPage, maxPage);
          const items = filtered.slice(cur * pageSize, (cur + 1) * pageSize);
          return (
            <>
              <List dense>
                {items.map(c => {
                  const checked = pickedIds.has(c.id);
                  return (
                    <ListItem key={c.id} onClick={()=>{
                      const n = new Set(pickedIds);
                      if (n.has(c.id)) n.delete(c.id); else n.add(c.id);
                      setPickedIds(n);
                    }}>
                      <ListItemText primary={`${c.name} ${formatPhoneKorean(c.phone)}`} />
                      <Box sx={{ color: checked ? '#1976d2' : '#999', fontWeight: 700 }}>{checked ? '✓' : '□'}</Box>
                    </ListItem>
                  );
                })}
              </List>
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button size="small" disabled={cur===0} onClick={()=> setCpPage(p=>Math.max(0, Math.min(maxPage, p-1)))}>이전</Button>
                <Button size="small" disabled={cur>=maxPage} onClick={()=> setCpPage(p=>Math.min(maxPage, p+1))}>다음</Button>
              </Stack>
            </>
          );
        })()}
      </DialogContent>
      <DialogActions>
        <Button onClick={()=>setContactPickerOpen(false)}>취소</Button>
        <Button variant="contained" onClick={async ()=>{
          if (!selectedGroup || pickedIds.size === 0) { setContactPickerOpen(false); return; }
          // 선택된 연락처를 멤버로 추가
          const chosen = contacts.filter(c => pickedIds.has(c.id));
          for (const c of chosen) {
            await setDoc(doc(collection(db, 'sms_groups', selectedGroup.id, 'members'), c.id), {
              contactId: c.id,
              nameSnapshot: c.name,
              phoneSnapshot: formatPhoneKorean(c.phone),
              phoneE164: normalizePhone(c.phone),
              addedAt: Date.now()
            });
          }
          // memberCount 반영(간단 계산)
          const updatedCount = (groups.find(g => g.id === selectedGroup.id)?.count || 0) + chosen.length;
          await updateDoc(doc(db, 'sms_groups', selectedGroup.id), { memberCount: updatedCount });
          setPickedIds(new Set());
          setContactPickerOpen(false);
        }}>추가</Button>
      </DialogActions>
    </Dialog>

    {/* 그룹명 수정 다이얼로그 */}
    <Dialog open={groupEditOpen} onClose={()=>setGroupEditOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>그룹명 수정</DialogTitle>
      <DialogContent>
        <TextField size="small" fullWidth value={groupEditName} onChange={(e)=> setGroupEditName(e.target.value)} autoFocus />
      </DialogContent>
      <DialogActions>
        <Button onClick={()=>setGroupEditOpen(false)}>취소</Button>
        <Button variant="contained" onClick={async ()=>{
          const name = (groupEditName || '').trim();
          if (!name || !editingGroupId) { setGroupEditOpen(false); return; }
          await updateDoc(doc(db, 'sms_groups', editingGroupId), { name });
          setGroupEditOpen(false);
        }}>저장</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default SmsAddressBookPanel;


